import { Capacitor } from '@capacitor/core'
import { supabase } from './supabase'

// Remote diagnostic logger — every call writes a row to public.debug_logs
// AND prints to the local console. Read remotely via /debug-logs (admin
// page) or the Supabase SQL editor. See migration
// 20260504000000_debug_logs.sql for the schema and RLS.
//
// Sessions: callers wrap a logical attempt (e.g. one receipt scan) with
// startDebugSession('receipt-scan'). All subsequent debugLog calls share
// that session_id until the next startDebugSession runs. The viewer
// groups events by session_id so a single attempt is one collapsible
// block instead of a flat firehose.
//
// Failure mode: writes are fire-and-forget — if the insert fails (offline,
// RLS rejection, network), we swallow the error so debug logging cannot
// itself break a user flow. The console.log still fires regardless.

const APP_VERSION = '1.0.11'

let currentSessionId: string | null = null

function generateSessionId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  return `sess-${Date.now()}-${Math.random().toString(36).slice(2)}`
}

export function startDebugSession(feature: string): string {
  currentSessionId = generateSessionId()
  console.log(`[DebugLogger] Started ${feature} session: ${currentSessionId}`)
  return currentSessionId
}

export function getCurrentSessionId(): string | null {
  return currentSessionId
}

interface DebugLogParams {
  feature:  string
  step:     string
  message?: string
  payload?: Record<string, unknown>
}

export async function debugLog(params: DebugLogParams): Promise<void> {
  const { feature, step, message, payload } = params
  const fullMessage = `[${feature}] ${step}${message ? `: ${message}` : ''}`
  console.log(fullMessage, payload ?? '')
  // Don't await — UI must not block on remote logging.
  void writeToSupabase(params)
}

async function writeToSupabase(params: DebugLogParams): Promise<void> {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return  // No user → no insert (RLS would reject anyway).

    const platform  = Capacitor.isNativePlatform() ? Capacitor.getPlatform() : 'web'
    const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown'
    const sessionId = currentSessionId ?? generateSessionId()

    await supabase.from('debug_logs').insert({
      session_id:  sessionId,
      user_id:     user.id,
      feature:     params.feature,
      step:        params.step,
      message:     params.message ?? null,
      payload:     params.payload ?? null,
      platform,
      user_agent:  userAgent,
      app_version: APP_VERSION,
    })
  } catch (err) {
    console.error('[DebugLogger] Failed to write log:', err)
  }
}

// Convenience for catch blocks. Adds a `-error` suffix to the step and
// extracts the message + truncated stack into payload so you can see
// where the throw came from in the viewer.
export async function debugLogError(
  feature: string,
  step:    string,
  err:     unknown,
): Promise<void> {
  const e: any = err
  await debugLog({
    feature,
    step:    `${step}-error`,
    message: e?.message ?? String(err),
    payload: {
      name:  e?.name,
      stack: typeof e?.stack === 'string' ? e.stack.slice(0, 2000) : null,
    },
  })
}
